import { FC, useMemo, useState } from 'react'
import type {
  InventoryImportOrder,
  OrderErrors,
  InventoryImportProduct,
} from '../../modules/Inventory/InventoryImportReview'
import {
  FormSelect,
  FormText,
  InventoryImportAccordionRow,
  PaginationNavButton,
  PaginationPageButton,
} from '..'

const pageSizes = [5, 10, 20, 50]

export const InventoryImportAccordionList: FC<{
  orders: InventoryImportOrder[]
  errors: OrderErrors[]
  identificationTypes: any[]
  typeShipment: any[]
  businessUnits: any[]
  cities: any[]
  pieceCategories: any[]
  onChangeOrder: (index: number, patch: Partial<InventoryImportOrder>) => void
  onChangeProduct: (
    orderIndex: number,
    productIndex: number,
    patch: Partial<InventoryImportProduct>
  ) => void
  onAddProduct: (orderIndex: number) => void
  onRemoveProduct: (orderIndex: number, productIndex: number) => void
  onRemoveOrder: (index: number) => void
  productsMap?: Record<string, any>
  productOptions?: any[]
}> = ({
  orders,
  errors,
  identificationTypes,
  typeShipment,
  businessUnits,
  cities,
  pieceCategories,
  onChangeOrder,
  onChangeProduct,
  onAddProduct,
  onRemoveProduct,
  onRemoveOrder,
  productsMap,
  productOptions,
}) => {
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [query, setQuery] = useState('')

  const normalized = (s: string) => (s || '').toString().toLowerCase()
  // Keep track of original indices through filtering
  const filteredRows = useMemo(() => {
    const q = normalized(query)
    const pairs = orders.map((r, i) => ({ r, i }))
    if (!q) return pairs
    return pairs.filter(({ r }) => {
      const orderFields = [
        r.orderNumber,
        r.idTypeAbbr,
        r.idNumber,
        r.name,
        r.phone,
        r.email,
        r.deliveryTypeName,
        r.buCode,
        r.cityCode,
        r.address,
        r.postalCode,
        r.notes,
      ]
      const productFields = (r.products || []).flatMap((p) => [
        p.productCode,
        p.productName,
      ])
      return [...orderFields, ...productFields].some((v) =>
        normalized(String(v || '')).includes(q)
      )
    })
  }, [orders, query])

  const total = filteredRows.length
  const totalPages = Math.max(1, Math.ceil(total / size))
  const safePage = Math.min(page, totalPages - 1)
  const start = safePage * size
  const end = Math.min(start + size, total)
  const pageRows = useMemo(
    () => filteredRows.slice(start, end),
    [filteredRows, start, end]
  )

  // Ventana deslizante: si hay más de 5 páginas, mostrar 4 números; si no, mostrar todas
  const windowSize = totalPages > 5 ? 4 : totalPages
  let windowStart = safePage
  if (windowStart + windowSize > totalPages)
    windowStart = Math.max(0, totalPages - windowSize)
  const pagesWindow = Array.from(
    { length: windowSize },
    (_, i) => windowStart + i
  )

  return (
    <div className="w-full">
      <div className="flex items-end justify-between mb-3 gap-3">
        <FormSelect<number>
          id={`accordion-page-size`}
          name={`accordion-page-size`}
          label=""
          selected={size}
          options={pageSizes}
          optionString={(opt) => `${opt}/pág`}
          onSelectOption={(opt) => {
            setSize(opt)
            setPage(0)
          }}
          className="!h-8"
          containerClassname="w-40"
        />
        <div className="flex-1 max-w-md">
          <FormText
            id="inv-search"
            name="inv-search"
            label="Buscar"
            placeholder="Pedido, cliente, producto…"
            value={query}
            onChange={(e: any) => {
              setQuery(e.target.value)
              setPage(0)
            }}
            className="!h-8"
          />
        </div>
      </div>

      {pageRows.map(({ r, i }) => {
        const globalIndex = i
        return (
          <InventoryImportAccordionRow
            key={globalIndex}
            order={orders[globalIndex]}
            index={globalIndex}
            errors={errors[globalIndex] || {}}
            identificationTypes={identificationTypes}
            typeShipment={typeShipment}
            businessUnits={businessUnits}
            cities={cities}
            pieceCategories={pieceCategories}
            onChangeOrder={(patch: Partial<InventoryImportOrder>) =>
              onChangeOrder(globalIndex, patch)
            }
            onChangeProduct={(
              pIdx: number,
              patch: Partial<InventoryImportProduct>
            ) => onChangeProduct(globalIndex, pIdx, patch)}
            onAddProduct={() => onAddProduct(globalIndex)}
            onRemoveProduct={(pIdx: number) =>
              onRemoveProduct(globalIndex, pIdx)
            }
            onRemoveOrder={() => onRemoveOrder(globalIndex)}
            productsMap={productsMap}
            productOptions={productOptions}
          />
        )
      })}

      <div className="flex items-center justify-between mt-3 text-sm">
        <div>
          Página {safePage + 1} de {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <PaginationNavButton
            direction="prev"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
          />
          {pagesWindow.map((p) => (
            <PaginationPageButton
              key={p}
              page={p + 1}
              active={p === safePage}
              onClick={() => setPage(p)}
            />
          ))}
          <PaginationNavButton
            direction="next"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1}
          />
        </div>
      </div>
    </div>
  )
}
